import dayjs from 'dayjs'
import { Value } from '../../elements'
import { Badge, Space, Tag } from 'antd'
import React from 'react'
import { Link } from 'react-router-dom'
import { IData, ISorterProps } from './interfaces'
import { IDeleteRecord } from '../../claims-request/list/interfaces'
import { RowActionMenu } from '../../../components/row-action-menu'
import { ColumnsType } from 'antd/es/table'
import { Stage } from '../../../components/activity-timeline/styles'
import type { StageData } from 'src/react-app/components/v2/activity-timeline'

const StageCell: React.FC<{ stage?: StageData }> = ({ stage }) => {
  if (!stage) {
    return <span>...</span>
  }

  return <Stage style={stage?.style}>{stage.name}</Stage>
}

function formatPrice(price: number): string {
  const formatter = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return formatter.format(price).replace('₽', '')
}

const formatPhoneNumber = (phoneNumber?: string) => {
  if (!phoneNumber) {
    return <Value data={undefined} />
  }
  const cleaned = ('' + phoneNumber).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})$/)
  if (match) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}-${match[5]}`
  }
  return phoneNumber
}

export const getColumns = (
  sorter: ISorterProps,
  leader: boolean,
  deleteRecord: (id: number, removeRequest: boolean) => Promise<IDeleteRecord>,
) => {
  const columnsArr: ColumnsType<IData> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: true,
      render: (val, row) => {
        const unreadMessages = row?.unread_messages
        if (unreadMessages && Number(unreadMessages) > 0) {
          return (
            <Space size={6} align="center">
              <span>{val}</span>
              <Badge
                count={Number(unreadMessages)}
                size="small"
                color="rgb(130, 30, 130)"
                offset={[0, -8]}
                title={`Непрочитанных сообщений в чате: ${unreadMessages}`}
              />
            </Space>
          )
        }
        return val
      },
    },
    {
      title: 'Источник',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      sorter: true,
    },
    {
      title: 'Стадия',
      dataIndex: 'stage',
      key: 'stage',
      sorter: true,
      width: 160,
      render: stage => <StageCell stage={stage} />,
    },
    {
      title: 'Дата рекламации',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      width: 120,
      render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
    },
    {
      title: 'Дата заказа',
      dataIndex: 'date_application',
      key: 'date_application',
      sorter: true,
      width: 120,
      render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
    },
    {
      title: 'Взял в работу',
      dataIndex: 'claim_responsible',
      key: 'claim_responsible',
      width: 220,
      render: val => <Value data={val} />,
    },
    {
      title: 'Ответственный дилер',
      dataIndex: 'dealer_code',
      key: 'dealer_code',
      width: 220,
      render: val => <Value data={val} />,
    },
    {
      title: 'Номер переделки',
      dataIndex: 'num_rework',
      key: 'num_rework',
      width: 220,
      render: val => <Value data={val} />,
    },
    {
      title: 'Сервисные работы',
      dataIndex: 'works',
      key: 'works',
      width: 70,
      render: (values?: { id: number; color: string }[]) => {
        if (values?.length) {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '3px',
              }}
            >
              {values.map(work => (
                <Link
                  key={work.id}
                  to={`/claim-work/${work.id}`}
                  onClick={e => e.stopPropagation()}
                >
                  <Tag color={work.color}>Работа №{work.id}</Tag>
                </Link>
              ))}
            </div>
          )
        }
        return <Value data={values} />
      },
    },
    {
      title: 'Дата готовности сервисной работы (план)',
      dataIndex: 'date_work_completion',
      key: 'date_work_completion',
      width: 100,
      render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
    },
    {
      title: 'Время готовности сервисной работы (план)',
      dataIndex: 'time_work_completion',
      key: 'time_work_completion',
      width: 80,
      render: (values?: string[]) => {
        if (values?.length) {
          return (
            <>
              {values.map((time, k) => (
                <Tag key={k}>{time}</Tag>
              ))}
            </>
          )
        }
        return <Value data={values} />
      },
    },
    {
      title: 'Дата и время готовности сервисной работы (факт)',
      dataIndex: 'datetime_work_completion',
      key: 'datetime_work_completion',
      width: 100,
      render: val => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : <Value data={val} />),
    },
    {
      title: 'Дата доставки',
      dataIndex: 'date_delivery',
      key: 'date_delivery',
      width: 220,
      render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
    },
    {
      title: 'Ответственный менеджер',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 200,
      render: val => <Value data={val} />,
    },
    {
      title: 'Номер тех. задания',
      dataIndex: 'num_tech',
      key: 'num_tech',
      width: 150,
    },
    {
      title: 'Новые сообщения',
      dataIndex: 'not_read',
      key: 'not_read',
      render: val => (+val > 0 ? <Badge count={+val} /> : '-'),
      align: 'center',
      sorter: true,
      width: 100,
    },
    {
      title: 'Адрес объекта',
      dataIndex: 'address',
      key: 'address',
      sorter: true,
      width: 300,
    },
    {
      title: 'ФИО',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 200,
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
      render: formatPhoneNumber,
      width: 150,
    },
    {
      title: 'Классификация',
      dataIndex: 'classification',
      key: 'classification',
      width: 150,
    },
    {
      title: 'Суть претензии',
      dataIndex: 'claim',
      key: 'claim',
      width: 400,
    },
    {
      title: 'Мероприятия дилера',
      dataIndex: 'dealer_event',
      key: 'dealer_event',
      width: 400,
    },
    {
      title: 'Требования дилера',
      dataIndex: 'dealer_requirement',
      key: 'dealer_requirement',
      width: 150,
    },
    {
      title: 'Ответственный за возникновение рекламации',
      dataIndex: 'reclamation_responsible_id',
      key: 'reclamation_responsible_id',
      width: 100,
    },
    {
      title: 'Ответственный за возникновение ущерба',
      dataIndex: 'compensation_responsible',
      key: 'compensation_responsible',
      width: 100,
    },
    {
      title: 'Исполнитель работ',
      dataIndex: 'work_executor',
      key: 'work_executor',
      width: 100,
    },
    {
      title: 'Плательщик',
      dataIndex: 'compensation_office_id',
      key: 'compensation_office_id',
      width: 100,
    },
    {
      title: 'Сотрудник, ответственный за возмещение ущерба',
      dataIndex: 'compensation_employee_id',
      key: 'compensation_employee_id',
      width: 100,
    },
    {
      title: 'Стоимость ущерба',
      dataIndex: 'price_total',
      key: 'price_total',
      width: 100,
      render: val => formatPrice(parseFloat(val) || 0),
    },
    {
      title: 'Сумма компенсации',
      dataIndex: 'compensation_amount',
      key: 'compensation_amount',
      width: 140,
      render: val => (val != null && val !== '' ? formatPrice(parseFloat(val) || 0) : <Value data={val} />),
    },
    /*
    {
      title: 'Акт решения по рекламации',
      dataIndex: 'reclamation_act',
      key: 'reclamation_act',
      width: 100,
    },
    */
    {
      title: 'Решение по рекламации',
      dataIndex: 'reclamation_decision',
      key: 'reclamation_decision',
      width: 100,
    },
    {
      title: 'Комментарий',
      dataIndex: 'comment',
      key: 'comment',
      width: 100,
    },
    {
      title: 'Причина отмены',
      dataIndex: 'cancel_reason',
      key: 'cancel_reason',
      width: 100,
    },
    {
      title: 'Дата завершения рекламации',
      dataIndex: 'close_date',
      key: 'close_date',
      width: 100,
      render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
    },
  ] as any[]

  const columnAction = {
    title: '',
    key: 'operation',
    className: 'no-edit',
    fixed: 'right',
    width: 50,
    align: 'center',
    render: (_, { id }) => {
      return (
        <span
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <RowActionMenu id={Number(id)} deleteRecord={deleteRecord as any} />
        </span>
      )
    },
  } as any
  /*
  const DeleteButton = ({ id, deleteRecord }) => {
    const [deleteState, setDeleteState] = useState<'idle' | 'confirming'>('idle')
    const [isDeleting, setIsDeleting] = useState(false)
    const [error, setError] = useState<boolean | string>(false)

    const handleInitialDelete = () => setDeleteState('confirming')

    const handleConfirmDelete = async (removeRequest: boolean = false) => {
      setIsDeleting(true)
      try {
        await deleteRecord(id, removeRequest)
          .then(() => {
            setDeleteState('idle')
          })
          .catch(() => {
            setError('Ошибка при удалении, попробуйте еще раз')
          })
      } finally {
        setIsDeleting(false)
      }
    }

    const handleCancel = () => setDeleteState('idle')
    return (
      <>
        <ButtonActionWrapper title="Удалить" onClick={handleInitialDelete}>
          <DeleteTwoTone />
        </ButtonActionWrapper>
        <Modal
          title="Подтверждение удаления"
          open={deleteState !== 'idle'}
          closable={!isDeleting}
          maskClosable={!isDeleting}
          keyboard={!isDeleting}
          onCancel={handleCancel}
          footer={[
            <Button
              disabled={isDeleting}
              type="primary"
              danger
              onClick={() => handleConfirmDelete(false)}
            >
              Удалить рекламацию
            </Button>,
            <Button
              disabled={isDeleting}
              type="primary"
              danger
              onClick={() => handleConfirmDelete(true)}
            >
              Удалить рекламацию и заявку
            </Button>,
            <Button disabled={isDeleting} onClick={handleCancel}>
              Нет
            </Button>,
          ]}
        >
          Вы уверены, что хотите удалить рекламацию № {id}?
          {error && <div style={{ color: 'red' }}>{error}</div>}
        </Modal>
      </>
    )
  }
  */
  if (leader) {
    columnsArr.push(columnAction)
  }
  return columnsArr.map(item => {
    if (item.key === sorter.sort_field) {
      item.sortOrder = sorter.sort_order
    } else {
      delete item.sortOrder
    }
    return item
  })
}
